import { useQuery } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'

import { PageBar, Content } from '../components/Layout'
import { Badge, Empty, ErrorBox, Loading, Panel } from '../components/ui'
import { api } from '../lib/api'
import {
  WEEKDAYS, addDays, addMonths, dateKey, dayTitle, isToday, monthGrid,
  monthTitle, sameMonth, startOfMonth, startOfWeek,
} from '../lib/dates'
import { gbp, timeOnly } from '../lib/format'
import type { Booking, Client, Room } from '../types'

type View = 'day' | 'week' | 'month'

const VIEWS: { key: View; label: string }[] = [
  { key: 'day', label: 'Day' },
  { key: 'week', label: 'Week' },
  { key: 'month', label: 'Month' },
]

/** The visible range for each view: where the grid starts and how many days it covers. */
function rangeFor(view: View, anchor: Date): { start: Date; days: number } {
  if (view === 'day') return { start: anchor, days: 1 }
  if (view === 'week') return { start: startOfWeek(anchor), days: 7 }
  return { start: startOfWeek(startOfMonth(anchor)), days: 42 }
}

export function Calendar() {
  const [params, setParams] = useSearchParams()
  const view = (params.get('view') as View) || 'month'
  const anchor = params.get('date') ? new Date(`${params.get('date')}T12:00:00`) : new Date()
  const [selected, setSelected] = useState<string>(dateKey(new Date()))

  const { start, days } = rangeFor(view, anchor)

  const { data, isLoading, error } = useQuery({
    queryKey: ['bookings', dateKey(start), days],
    queryFn: () => api.get<Booking[]>(`/bookings?start=${dateKey(start)}&days=${days}`),
  })
  const rooms = useQuery({ queryKey: ['rooms'], queryFn: () => api.get<Room[]>('/rooms') })
  const clients = useQuery({ queryKey: ['clients'], queryFn: () => api.get<Client[]>('/clients') })

  // Keep the agenda pointing at a day that is actually on screen. Navigating to another
  // month and still seeing today's agenda reads like a bug.
  useEffect(() => {
    const first = dateKey(start)
    const last = dateKey(addDays(start, days - 1))
    if (selected >= first && selected <= last) return
    const todayKey = dateKey(new Date())
    setSelected(todayKey >= first && todayKey <= last ? todayKey : first)
  }, [start, days, selected])

  const byDay = useMemo(() => {
    const map = new Map<string, Booking[]>()
    for (const booking of data ?? []) {
      const key = booking.starts_at.slice(0, 10)
      map.set(key, [...(map.get(key) ?? []), booking])
    }
    return map
  }, [data])

  const go = (next: Date, nextView: View = view) =>
    setParams({ view: nextView, date: dateKey(next) })

  const step = (direction: number) => {
    if (view === 'month') go(addMonths(anchor, direction))
    else go(addDays(anchor, direction * (view === 'week' ? 7 : 1)))
  }

  const nameOf = (id: number | null) =>
    id === null ? 'Held' : (clients.data?.find((c) => c.id === id)?.name ?? 'Held')
  const roomOf = (id: number) => rooms.data?.find((r) => r.id === id)?.name ?? ''

  const selectedDate = new Date(`${selected}T12:00:00`)
  const agenda = byDay.get(selected) ?? []

  const title =
    view === 'month' ? monthTitle(anchor)
      : view === 'week' ? `${dayTitle(startOfWeek(anchor))} — ${dayTitle(addDays(startOfWeek(anchor), 6))}`
      : dayTitle(anchor)

  return (
    <>
      <PageBar
        title="Calendar"
        actions={
          <>
            <button className="btn ghost" type="button" onClick={() => go(new Date())}>Today</button>
            <button className="btn ghost" type="button" onClick={() => step(-1)} aria-label="Previous">←</button>
            <button className="btn ghost" type="button" onClick={() => step(1)} aria-label="Next">→</button>
            <div className="viewtabs">
              {VIEWS.map((v) => (
                <button
                  key={v.key}
                  type="button"
                  className={`viewtab ${view === v.key ? 'on' : ''}`}
                  onClick={() => go(anchor, v.key)}
                >
                  {v.label}
                </button>
              ))}
            </div>
            <Link className="btn" to="/bookings/new">New booking</Link>
          </>
        }
      />
      <Content>
        <div className="calhead">
          <h2 className="calmonth">{title}</h2>
          {data && <span className="muted">{data.length} session{data.length === 1 ? '' : 's'} in view</span>}
        </div>

        {isLoading && <Loading />}
        {error && <ErrorBox error={error} />}

        {data && (
          <div className="calwrap">
            <div className="calmain">
              {view === 'month' && (
                <div className="cal month">
                  {WEEKDAYS.map((d) => <div key={d} className="cal-head">{d}</div>)}
                  {monthGrid(anchor).map((day) => {
                    const key = dateKey(day)
                    const events = byDay.get(key) ?? []
                    const outside = !sameMonth(day, anchor)
                    return (
                      <button
                        key={key}
                        type="button"
                        className={[
                          'cal-day',
                          outside ? 'outside' : '',
                          isToday(day) ? 'today' : '',
                          selected === key ? 'picked' : '',
                          day.getDay() === 0 || day.getDay() === 6 ? 'weekend' : '',
                        ].filter(Boolean).join(' ')}
                        onClick={() => setSelected(key)}
                      >
                        <span className="cal-num">{day.getDate()}</span>
                        <span className="cal-body">
                          {events.slice(0, 3).map((b) => (
                            <span key={b.id} className={`chip s-${b.status}`}>
                              <b>{timeOnly(b.starts_at)}</b> {nameOf(b.client_id)}
                            </span>
                          ))}
                          {events.length > 3 && (
                            <span className="chip more">+{events.length - 3} more</span>
                          )}
                        </span>
                      </button>
                    )
                  })}
                </div>
              )}

              {view === 'week' && (
                <div className="cal week">
                  {WEEKDAYS.map((d) => <div key={d} className="cal-head">{d}</div>)}
                  {Array.from({ length: 7 }, (_, i) => addDays(startOfWeek(anchor), i)).map((day) => {
                    const key = dateKey(day)
                    const events = byDay.get(key) ?? []
                    return (
                      <button
                        key={key}
                        type="button"
                        className={`cal-day tall ${isToday(day) ? 'today' : ''} ${selected === key ? 'picked' : ''}`}
                        onClick={() => setSelected(key)}
                      >
                        <span className="cal-num">{day.getDate()}</span>
                        <span className="cal-body">
                          {events.map((b) => (
                            <span key={b.id} className={`chip s-${b.status}`}>
                              <b>{timeOnly(b.starts_at)}–{timeOnly(b.ends_at)}</b>
                              {nameOf(b.client_id)}
                              <i>{roomOf(b.room_id)}</i>
                            </span>
                          ))}
                        </span>
                      </button>
                    )
                  })}
                </div>
              )}

              {view === 'day' && (
                <Panel>
                  {(byDay.get(dateKey(anchor)) ?? []).length ? (
                    <table className="cards">
                      <thead>
                        <tr><th style={{ width: 130 }}>Time</th><th>Who</th><th>Room</th><th>Value</th><th>Status</th></tr>
                      </thead>
                      <tbody>
                        {(byDay.get(dateKey(anchor)) ?? []).map((b) => (
                          <tr key={b.id}>
                            <td className="when">
                              <Link to={`/bookings/${b.id}`}>{timeOnly(b.starts_at)}–{timeOnly(b.ends_at)}</Link>
                            </td>
                            <td className="who">{nameOf(b.client_id)}</td>
                            <td className="muted" data-label="Room">{roomOf(b.room_id)}</td>
                            <td data-label="Value">{gbp(b.quoted_pence)}</td>
                            <td><Badge kind="b" value={b.status} /></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <Empty>Nothing booked on {dayTitle(anchor)}.</Empty>
                  )}
                </Panel>
              )}
            </div>

            <aside className="agenda">
              <div className="agenda-head">
                <span className="stat-label">Agenda</span>
                <h3>{dayTitle(selectedDate)}</h3>
                <span className="muted">{agenda.length} session{agenda.length === 1 ? '' : 's'}</span>
              </div>
              {agenda.length ? (
                <div className="agenda-list">
                  {agenda.map((b) => (
                    <Link key={b.id} to={`/bookings/${b.id}`} className={`agenda-item s-${b.status}`}>
                      <div className="agenda-time">{timeOnly(b.starts_at)}–{timeOnly(b.ends_at)}</div>
                      <div className="agenda-who">{nameOf(b.client_id)}</div>
                      <div className="agenda-meta">
                        {roomOf(b.room_id)} · {gbp(b.quoted_pence)}
                        {b.engineer && ` · ${b.engineer}`}
                      </div>
                      <Badge kind="b" value={b.status} />
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="agenda-empty">
                  <p>Nothing booked this day.</p>
                  <Link className="btn ghost small" to={`/bookings/new?day=${selected}`}>Book it</Link>
                </div>
              )}
            </aside>
          </div>
        )}

        <p className="hint">
          Empty weekday hours are perishable inventory — an unbooked Tuesday earns nothing and
          can&rsquo;t be sold later. That&rsquo;s what the workshop and off-peak ideas are for.
        </p>
      </Content>
    </>
  )
}
